import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { EnvelopeSimple, Copy, Trash, ShieldStar, ArrowLeft, Plus, CheckCircle, Clock, XCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';

const STATUS_META = {
    pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-800', icon: Clock },
    redeemed: { label: 'Redeemed', cls: 'bg-emerald-50 text-emerald-800', icon: CheckCircle },
    expired: { label: 'Expired', cls: 'bg-slate-100 text-slate-500', icon: XCircle },
};

function InviteForm({ onDone }) {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.post('/admin/invites', { email: email.trim().toLowerCase(), name: name.trim() });
            const link = `${window.location.origin}/accept-invite/${data.token}`;
            await navigator.clipboard.writeText(link);
            toast.success('Invite created — link copied to clipboard');
            setEmail(''); setName('');
            onDone?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not create invite');
        } finally { setSaving(false); }
    };
    return (
        <form onSubmit={submit} className="space-y-3" data-testid="invite-form">
            <div><Label>Full name</Label>
                <Input data-testid="invite-name" value={name} onChange={e => setName(e.target.value)} placeholder="Sofia Rivera" /></div>
            <div><Label>Email</Label>
                <Input data-testid="invite-email" required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="sofia@example.com" /></div>
            <div className="text-[11px] text-slate-500 leading-relaxed">
                We&apos;ll generate a private invite link — copy it and send however you like. The link expires in 14 days.
            </div>
            <Button data-testid="invite-submit" type="submit" disabled={saving || !email} className="w-full rounded-full bg-primary text-primary-foreground">
                {saving ? 'Creating…' : 'Create invite link'}
            </Button>
        </form>
    );
}

export default function AdminInvites() {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [del, setDel] = useState(null);
    const { data: invites = [], isLoading } = useQuery({
        queryKey: ['admin-invites'],
        queryFn: async () => (await api.get('/admin/invites')).data,
    });

    const copyLink = (token) => {
        const link = `${window.location.origin}/accept-invite/${token}`;
        navigator.clipboard.writeText(link);
        toast.success('Link copied');
    };
    const doRevoke = async () => {
        try {
            await api.delete(`/admin/invites/${del.invite_id}`);
            toast.success('Invite revoked');
            setDel(null);
            qc.invalidateQueries({ queryKey: ['admin-invites'] });
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not revoke'); }
    };

    return (
        <div className="px-6 md:px-10 py-8 max-w-5xl" data-testid="admin-invites-root">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1.5">
                <ShieldStar size={12} weight="duotone" /> Tanow staff
            </div>
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900 flex items-center gap-3">
                        <EnvelopeSimple size={30} className="text-primary" /> Invite a coordinator
                    </h1>
                    <p className="text-slate-600 mt-2 max-w-2xl">Tanow is invite-only. Generate a private link for each new coordinator; they sign in with the same Google account.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-invite-btn" className="rounded-full bg-primary text-primary-foreground">
                            <Plus size={16} className="mr-1" /> New invite
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-heading text-2xl">Invite a coordinator</DialogTitle></DialogHeader>
                        <InviteForm onDone={() => { setOpen(false); qc.invalidateQueries({ queryKey: ['admin-invites'] }); }} />
                    </DialogContent>
                </Dialog>
            </div>

            <div className="rounded-xl border border-slate-200 bg-card shadow-sm overflow-hidden">
                <div className="grid grid-cols-[1.4fr_1fr_150px_130px_130px] px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    <div>Coordinator</div><div>Invited by</div><div>Created</div><div>Status</div><div className="text-right">Actions</div>
                </div>
                {isLoading ? (
                    <div className="px-5 py-8 text-sm text-slate-500 text-center">Loading…</div>
                ) : invites.length === 0 ? (
                    <div className="px-5 py-10 text-sm text-slate-500 text-center">No invites yet. Tap <b>New invite</b> to add one.</div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {invites.map(inv => {
                            const meta = STATUS_META[inv.status] || STATUS_META.pending;
                            const Icon = meta.icon;
                            return (
                                <div key={inv.invite_id} data-testid="invite-row" className="grid grid-cols-[1.4fr_1fr_150px_130px_130px] px-5 py-3 items-center text-sm">
                                    <div className="min-w-0">
                                        <div className="text-slate-900 truncate font-medium">{inv.name || inv.email}</div>
                                        {inv.name && <div className="text-[11px] text-slate-500 truncate">{inv.email}</div>}
                                    </div>
                                    <div className="text-slate-600 truncate">{inv.invited_by_name}</div>
                                    <div className="text-slate-500 text-[12px]">{new Date(inv.created_at).toLocaleDateString()}</div>
                                    <div>
                                        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 ${meta.cls}`}>
                                            <Icon size={10} weight="fill" /> {meta.label}
                                        </span>
                                    </div>
                                    <div className="text-right space-x-2">
                                        {inv.status === 'pending' && (
                                            <>
                                                <Button size="sm" variant="outline" data-testid="invite-copy" onClick={() => copyLink(inv.token)} className="rounded-full">
                                                    <Copy size={14} className="mr-1" /> Copy link
                                                </Button>
                                                <Button size="sm" variant="ghost" data-testid="invite-revoke" onClick={() => setDel(inv)} className="rounded-full text-red-600">
                                                    <Trash size={14} />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Link to="/admin/households" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mt-6">
                <ArrowLeft size={14} /> Back to all households
            </Link>

            <ConfirmDialog
                open={!!del} onOpenChange={v => !v && setDel(null)}
                title="Revoke this invite?"
                description={del ? `The link for "${del.email}" will stop working. This can't be undone.` : ''}
                onConfirm={doRevoke}
            />
        </div>
    );
}
